class Impulsa3MediaCardsCarousel {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector('.impulsa3-media-cards-carousel__slider');
    this.track = section.querySelector('.impulsa3-media-cards-carousel__track');
    this.slides = Array.from(section.querySelectorAll('.impulsa3-media-cards-carousel__slide'));
    this.prevButton = section.querySelector('.impulsa3-media-cards-carousel__control--prev');
    this.nextButton = section.querySelector('.impulsa3-media-cards-carousel__control--next');
    this.pagination = section.querySelector('.impulsa3-media-cards-carousel__pagination');
    this.filters = section.querySelectorAll('.impulsa3-media-cards-carousel-filter');
    this.currentPage = 0;
    this.totalPages = 1;
    this.slidesPerView = 1;
    this.slideStep = 0;

    this.isDragging = false;
    this.startX = 0;
    this.startTranslate = 0;
    this.currentTranslate = 0;
    this.pointerId = null;

    if (!this.section || !this.slider || !this.track || !this.slides.length) return;

    this.selectedFilter = 'all';
    this.setup();
    this.bindEvents();
    this.updateMetrics();
    this.goToPage(0, false);
    this.filterCards(this.selectedFilter);
  }

  setup() {
    this.section.setAttribute('data-impulsa3-carousel-initialized', 'true');
    this.track.setAttribute('data-impulsa3-ready', 'true');

    this.slides.forEach((slide, index) => {
      slide.setAttribute('data-impulsa3-slide-index', index);
      const tags = slide.getAttribute('data-tags') || '';
      slide.setAttribute('data-tags', tags.toLowerCase());
    });
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevClick);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextClick);
    }

    if (this.pagination) {
      this.pagination.addEventListener('click', this.handlePaginationClick);
    }

    window.addEventListener('resize', this.handleResize);

    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
    this.track.addEventListener('lostpointercapture', this.handlePointerUp);

    this.filters.forEach((filter) => {
      filter.addEventListener('click', (e) => {
        const filterValue = e.target.dataset.filter;
        this.selectedFilter = filterValue;
        this.filterCards(filterValue);
      });
    });
  }

  destroy() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }

    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }

    if (this.pagination) {
      this.pagination.removeEventListener('click', this.handlePaginationClick);
    }

    window.removeEventListener('resize', this.handleResize);

    this.track.removeEventListener('pointerdown', this.handlePointerDown);
    this.track.removeEventListener('pointermove', this.handlePointerMove);
    this.track.removeEventListener('pointerup', this.handlePointerUp);
    this.track.removeEventListener('pointercancel', this.handlePointerUp);
    this.track.removeEventListener('lostpointercapture', this.handlePointerUp);
  }

  getVisibleSlides() {
    return this.slides.filter((slide) => slide.style.display !== 'none');
  }

  filterCards(filter) {
    this.filters.forEach((filterBtn) => {
      filterBtn.classList.remove('active');
      if (filterBtn.dataset.filter === filter) {
        filterBtn.classList.add('active');
      }
    });

    this.slides.forEach((card) => {
      const tags = (card.getAttribute('data-tags') || '')
        .split(',')
        .map((tag) => tag.trim().toLowerCase());

      if (filter === 'all' || tags.includes(filter)) {
        card.style.display = 'block';
      } else {
        card.style.display = 'none';
      }
    });

    this.currentPage = 0;
    this.updateMetrics();
    this.goToPage(0, false);
  }

  handlePrevClick = () => {
    this.goToPage(this.currentPage - 1);
  };

  handleNextClick = () => {
    this.goToPage(this.currentPage + 1);
  };

  handlePaginationClick = (event) => {
    const button = event.target.closest('.impulsa3-media-cards-carousel__pagination-button');

    if (!button) return;

    const page = Number(button.dataset.page);

    if (Number.isNaN(page)) return;

    this.goToPage(page);
  };

  handleResize = () => {
    const previousPage = this.currentPage;
    this.updateMetrics();
    this.goToPage(previousPage, false);
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.totalPages <= 1) return;

    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startTranslate = this.getCurrentTranslate();
    this.currentTranslate = this.startTranslate;

    this.track.classList.add('is-dragging');
    this.track.style.transition = 'none';

    try {
      this.track.setPointerCapture(event.pointerId);
    } catch (error) {
      // No action needed
    }
  };

  handlePointerMove = (event) => {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    const deltaX = event.clientX - this.startX;
    const minTranslate = -this.getMaxOffset();
    const maxTranslate = 0;
    const nextTranslate = this.clamp(this.startTranslate + deltaX, minTranslate, maxTranslate);

    this.currentTranslate = nextTranslate;
    this.setTranslate(nextTranslate);
  };

  handlePointerUp = (event) => {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    this.track.style.transition = '';

    try {
      this.track.releasePointerCapture(event.pointerId);
    } catch (error) {
      // No action needed
    }

    const movedBy = this.currentTranslate - this.startTranslate;
    const threshold = Math.max(this.slideStep * 0.2, 40);

    if (movedBy <= -threshold) {
      this.goToPage(this.currentPage + 1);
      return;
    }

    if (movedBy >= threshold) {
      this.goToPage(this.currentPage - 1);
      return;
    }

    this.goToPage(this.currentPage);
  };

  updateMetrics() {
    const visibleSlides = this.getVisibleSlides();

    this.slidesPerView = this.getSlidesPerView();
    this.slideStep = this.getSlideStep();

    const visibleCount = visibleSlides.length;
    const maxPage = Math.max(0, Math.ceil(visibleCount / this.slidesPerView) - 1);
    this.totalPages = maxPage + 1;

    if (this.prevButton) {
      this.prevButton.style.display = '';
    }

    if (this.nextButton) {
      this.nextButton.style.display = '';
    }

    if (this.pagination) {
      this.pagination.style.display = this.totalPages <= 1 ? 'none' : '';
      this.renderPagination();
    }
  }

  getSlidesPerView() {
    const visibleSlides = this.getVisibleSlides();

    if (!visibleSlides.length) return 1;

    const sliderWidth = this.slider.getBoundingClientRect().width;
    const firstSlide = visibleSlides[0];
    const slideWidth = firstSlide.getBoundingClientRect().width;

    if (!sliderWidth || !slideWidth) return 1;

    const computedTrackStyle = window.getComputedStyle(this.track);
    const gap = parseFloat(computedTrackStyle.columnGap || computedTrackStyle.gap || '0') || 0;
    const estimatedSlidesPerView = Math.max(1, Math.round((sliderWidth + gap) / (slideWidth + gap)));

    return estimatedSlidesPerView;
  }

  getSlideStep() {
    const visibleSlides = this.getVisibleSlides();

    if (!visibleSlides.length) return 0;

    if (visibleSlides.length === 1) {
      return visibleSlides[0].getBoundingClientRect().width;
    }

    const firstSlide = visibleSlides[0];
    const secondSlide = visibleSlides[1];

    if (!firstSlide || !secondSlide) {
      return firstSlide ? firstSlide.getBoundingClientRect().width : 0;
    }

    return secondSlide.offsetLeft - firstSlide.offsetLeft;
  }

  getPageOffset(page) {
    const visibleSlides = this.getVisibleSlides();
    const targetIndex = page * this.slidesPerView;
    const targetSlide = visibleSlides[targetIndex];

    if (!targetSlide) {
      return this.getMaxOffset();
    }

    return Math.min(targetSlide.offsetLeft, this.getMaxOffset());
  }

  getMaxOffset() {
    const visibleSlides = this.getVisibleSlides();

    if (!visibleSlides.length) return 0;

    const lastSlide = visibleSlides[visibleSlides.length - 1];
    const sliderRect = this.slider.getBoundingClientRect();
    const lastSlideRect = lastSlide.getBoundingClientRect();

    const contentWidth = lastSlide.offsetLeft + lastSlideRect.width;

    return Math.max(0, contentWidth - sliderRect.width);
  }

  getCurrentTranslate() {
    const computedStyle = window.getComputedStyle(this.track);
    const transform = computedStyle.transform;

    if (!transform || transform === 'none') return 0;

    try {
      const matrix = new DOMMatrixReadOnly(transform);
      return matrix.m41;
    } catch (error) {
      return 0;
    }
  }

  setTranslate(value) {
    this.track.style.transform = `translate3d(${value}px, 0, 0)`;
  }

  goToPage(page, animate = true) {
    const boundedPage = this.clamp(page, 0, this.totalPages - 1);
    const offset = this.getPageOffset(boundedPage);

    this.currentPage = boundedPage;

    if (!animate) {
      this.track.style.transition = 'none';
      this.setTranslate(-offset);

      requestAnimationFrame(() => {
        this.track.style.transition = '';
      });
    } else {
      this.setTranslate(-offset);
    }

    this.updateControls();
    this.updatePagination();
  }

  updateControls() {
    if (this.prevButton) {
      const isDisabled = this.currentPage <= 0 || this.totalPages <= 1;
      this.prevButton.disabled = isDisabled;
      this.prevButton.setAttribute('aria-disabled', String(isDisabled));
    }

    if (this.nextButton) {
      const isDisabled = this.currentPage >= this.totalPages - 1 || this.totalPages <= 1;
      this.nextButton.disabled = isDisabled;
      this.nextButton.setAttribute('aria-disabled', String(isDisabled));
    }
  }

  renderPagination() {
    if (!this.pagination) return;

    if (this.totalPages <= 1) {
      this.pagination.innerHTML = '';
      return;
    }

    const buttonsMarkup = Array.from({ length: this.totalPages }, (_, pageIndex) => {
      const label = `Ir a la página ${pageIndex + 1}`;

      return `
        <button
          type="button"
          class="impulsa3-media-cards-carousel__pagination-button${pageIndex === this.currentPage ? ' is-active' : ''}"
          data-page="${pageIndex}"
          aria-label="${label}"
          aria-current="${pageIndex === this.currentPage ? 'true' : 'false'}"
        >
          <span class="impulsa3-media-cards-carousel__pagination-dot"></span>
        </button>
      `;
    }).join('');

    this.pagination.innerHTML = buttonsMarkup;
  }

  updatePagination() {
    if (!this.pagination) return;

    const buttons = this.pagination.querySelectorAll('.impulsa3-media-cards-carousel__pagination-button');

    buttons.forEach((button, index) => {
      const isActive = index === this.currentPage;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', String(isActive));
    });
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

class Impulsa3MediaCardsCarouselManager {
  constructor() {
    this.instances = new Map();
    this.init();
  }

  init() {
    const sections = document.querySelectorAll('.impulsa3-media-cards-carousel');

    sections.forEach((section) => {
      this.mount(section);
    });

    document.addEventListener('shopify:section:load', this.handleSectionLoad);
    document.addEventListener('shopify:section:unload', this.handleSectionUnload);
  }

  mount(section) {
    if (!section || this.instances.has(section)) return;

    const instance = new Impulsa3MediaCardsCarousel(section);
    this.instances.set(section, instance);
  }

  unmount(section) {
    const instance = this.instances.get(section);

    if (!instance) return;

    instance.destroy();
    this.instances.delete(section);
  }

  handleSectionLoad = (event) => {
    const directSection = event.target;
    const carouselSection = directSection.classList?.contains('impulsa3-media-cards-carousel')
      ? directSection
      : directSection.querySelector('.impulsa3-media-cards-carousel');

    if (carouselSection) {
      this.mount(carouselSection);
    }
  };

  handleSectionUnload = (event) => {
    const directSection = event.target;
    const carouselSection = directSection.classList?.contains('impulsa3-media-cards-carousel')
      ? directSection
      : directSection.querySelector('.impulsa3-media-cards-carousel');

    if (carouselSection) {
      this.unmount(carouselSection);
    }
  };
}

if (!window.impulsa3MediaCardsCarouselManager) {
  window.impulsa3MediaCardsCarouselManager = new Impulsa3MediaCardsCarouselManager();
}